import { z } from 'zod';
import { domainSchema, domainStatusSchema, hostnameSchema, type Domain } from './tenancy.js';

/**
 * A store's own domain (spec milestone ג; docs/DOMAINS.md). The owner adds a hostname, sets the
 * DNS records the screen shows, and the worker checks them until the address serves the store
 * over HTTPS. Enum values are shared with the database; a test checks that they match exactly.
 */

/** DNS record types the instructions and the observation speak about. */
export const DNS_RECORD_TYPES = ['CNAME', 'A', 'AAAA', 'TXT'] as const;
export const dnsRecordTypeSchema = z.enum(DNS_RECORD_TYPES);
export type DnsRecordType = z.infer<typeof dnsRecordTypeSchema>;

/** One record the owner creates at their DNS provider. `name` is the full host name. */
export const dnsRecordSchema = z.object({
  type: dnsRecordTypeSchema,
  name: z.string(),
  value: z.string(),
});
export type DnsRecord = z.infer<typeof dnsRecordSchema>;

/**
 * The ways to connect an address.
 * - CNAME: one record pointing at the store's system address. It routes the address to the
 *   platform and proves who asked for it, and it follows the platform if the server ever moves.
 * - CNAME_TXT: under path addressing the stores share one host, so a CNAME to it routes the
 *   address but proves nothing: the TXT record with the store's token is needed as well.
 * - A_TXT: for a root address (`shop.co.il`), where DNS allows no CNAME: address records to the
 *   platform's current addresses, and a TXT record carrying the store's token as the proof.
 */
export const DNS_SETUP_KINDS = ['CNAME', 'CNAME_TXT', 'A_TXT'] as const;
export const dnsSetupKindSchema = z.enum(DNS_SETUP_KINDS);
export type DnsSetupKind = z.infer<typeof dnsSetupKindSchema>;

export const dnsSetupOptionSchema = z.object({
  kind: dnsSetupKindSchema,
  records: z.array(dnsRecordSchema),
});
export type DnsSetupOption = z.infer<typeof dnsSetupOptionSchema>;

export const domainDnsInstructionsSchema = z.object({
  options: z.array(dnsSetupOptionSchema),
  /**
   * False when the platform's addresses could not be read just now: the A_TXT option then lists
   * the TXT record only, and the screen says to try again shortly.
   */
  addressesKnown: z.boolean(),
});
export type DomainDnsInstructions = z.infer<typeof domainDnsInstructionsSchema>;

/** What the last check saw in DNS. Shown to the owner so a typo is easy to find. */
export const domainObservationSchema = z.object({
  /** The CNAME chain from the address, in order; empty when it has none. */
  cname: z.array(z.string()).max(16),
  /** The addresses the hostname resolves to (IPv4 and IPv6). */
  addresses: z.array(z.string()).max(32),
  /** The ownership TXT record carries this store's token. */
  txtMatches: z.boolean(),
  /** CAA records exist and none lets our certificate authority issue. */
  caaBlocked: z.boolean(),
});
export type DomainObservation = z.infer<typeof domainObservationSchema>;

export const domainDetailSchema = z.object({
  domain: domainSchema,
  /** Null for the system address, which needs no DNS work. */
  dns: domainDnsInstructionsSchema.nullable(),
  /** Null until the first check ran. */
  observed: domainObservationSchema.nullable(),
});
export type DomainDetail = z.infer<typeof domainDetailSchema>;

/**
 * A store's addresses. `certificateIssuing` says how an own domain gets its certificate here:
 * true when the staff ask for it with a button once the address points at the platform
 * (adr/0029), false when nothing on the screen does it (an edge obtains it by itself, or
 * whoever runs the server does).
 */
export const domainListResponseSchema = z.object({
  items: z.array(domainSchema),
  certificateIssuing: z.boolean(),
});
export type DomainListResponse = z.infer<typeof domainListResponseSchema>;

/**
 * A certificate can be asked for once the address points at the platform and its ownership is
 * proven (VERIFYING), and again for an active address whose certificate stopped being accepted.
 */
export function canIssueCertificate(domain: Pick<Domain, 'kind' | 'status' | 'reason'>): boolean {
  if (domain.kind !== 'CUSTOM') return false;
  return (
    domain.status === 'VERIFYING' || (domain.status === 'ACTIVE' && domain.reason === 'TLS_ERROR')
  );
}

/**
 * How asking for a certificate ended.
 * - ISSUED: the address answers over HTTPS; the next check makes it active.
 * - FAILED: the server refused or could not; `failure` says which, `output` is what it printed.
 * - TIMEOUT: the server did not answer in time. It may still finish; the checks go on.
 */
export const CERTIFICATE_ISSUE_RESULTS = ['ISSUED', 'FAILED', 'TIMEOUT'] as const;
export const certificateIssueResultSchema = z.enum(CERTIFICATE_ISSUE_RESULTS);
export type CertificateIssueResult = z.infer<typeof certificateIssueResultSchema>;

/**
 * Why the server did not issue a certificate.
 * - NOT_ALLOWED: the address is not a proven address of a store.
 * - HOST_TAKEN: another site on the server already answers to this name.
 * - WEB_SERVER: the web server refused the new site.
 * - CERTIFICATE: the certificate authority refused, or could not reach the address.
 * - UNKNOWN: anything else.
 */
export const CERTIFICATE_FAILURES = [
  'NOT_ALLOWED',
  'HOST_TAKEN',
  'WEB_SERVER',
  'CERTIFICATE',
  'UNKNOWN',
] as const;
export const certificateFailureSchema = z.enum(CERTIFICATE_FAILURES);
export type CertificateFailure = z.infer<typeof certificateFailureSchema>;

export const issueCertificateResponseSchema = z.object({
  result: certificateIssueResultSchema,
  /** Set when `result` is FAILED. */
  failure: certificateFailureSchema.nullable(),
  /** The last lines the server printed, for whoever looks into a failure. */
  output: z.string().max(4000).nullable(),
  detail: domainDetailSchema,
});
export type IssueCertificateResponse = z.infer<typeof issueCertificateResponseSchema>;

export const addDomainRequestSchema = z.object({ hostname: hostnameSchema });
export type AddDomainRequest = z.infer<typeof addDomainRequestSchema>;

/** A store may hold this many own domains (root and www of a few names). */
export const MAX_CUSTOM_DOMAINS_PER_STORE = 5;

/** The platform's monitoring list: every store's own domains, the ones needing attention first. */
export const platformDomainSchema = domainSchema.omit({ storeId: true }).extend({
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
});
export type PlatformDomain = z.infer<typeof platformDomainSchema>;

export const platformDomainsQuerySchema = z.object({ status: domainStatusSchema.optional() });
export const platformDomainsResponseSchema = z.object({
  items: z.array(platformDomainSchema),
  /** How many own domains are in each status, across all stores. */
  counts: z.record(domainStatusSchema, z.number().int()),
});
export type PlatformDomainsResponse = z.infer<typeof platformDomainsResponseSchema>;

/** The edge asks before it obtains a certificate for a hostname (Caddy on-demand TLS `ask`). */
export const edgeTlsQuerySchema = z.object({
  domain: z.string().max(253),
  key: z.string().max(256).optional(),
});

/** The worker job: check one domain's DNS and certificate. */
export const domainCheckJobSchema = z.object({ tenantId: z.uuid(), domainId: z.uuid() });
export type DomainCheckJob = z.infer<typeof domainCheckJobSchema>;
